import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { ApiError, type ResponseEnvelope } from '@/types/api';
import keycloak from '@/lib/auth/keycloak';

const baseURL = import.meta.env.VITE_API_BASE_URL ?? '';

export const api: AxiosInstance = axios.create({
  baseURL,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = keycloak.token;
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ResponseEnvelope<unknown>>) => {
    // Network-level failure (no response received)
    if (!error.response) {
      throw new ApiError(0, [
        {
          code: 'NETWORK_ERROR',
          message:
            'Unable to reach the server. Check your connection and try again.',
          type: 'ERROR',
        },
      ]);
    }

    const { status, data } = error.response;

    // Session expired — redirect to Authmatech Identity login
    if (status === 401) {
      keycloak.login({ redirectUri: window.location.origin });
      throw new ApiError(status, [
        { code: 'UNAUTHORIZED', message: 'Session expired. Redirecting to sign-in…', type: 'ERROR' },
      ]);
    }

    // Server returned a structured envelope — surface the message directly
    if (data && typeof data === 'object' && 'messages' in data && Array.isArray(data.messages)) {
      throw new ApiError(status, data.messages);
    }

    // Fallback per status code
    const fallbackMessages: Record<number, string> = {
      400: 'Invalid request. Please check your filters or input and try again.',
      403: 'You do not have permission to perform this action.',
      404: 'The requested resource was not found.',
      500: 'An unexpected server error occurred. Please try again later.',
      502: 'The server is temporarily unavailable. Please try again in a moment.',
      503: 'Service unavailable. Maintenance may be in progress.',
    };

    throw new ApiError(status, [
      {
        code: `HTTP_${status}`,
        message: fallbackMessages[status] ?? `Request failed with status ${status}.`,
        type: 'ERROR',
      },
    ]);
  },
);
